import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TimeSelectorRow, SearchBox, GeneralSelect } from 'sharedComponents';
import EventTypeFilter from './EventTypeFilter';
import { eventStatus, EventTypeAssetApplicability } from '../constants';
import { getEventStatusOptions, getDefaultEventStatus } from '../helpers';
import { selectState } from 'helpers/filterHelper';
import { apiCallStatus } from 'helpers/constants';
import { getSelectedEventData, isCrossOrgNotification, isEventGroupNotification } from './helpers';

const EventLogFilterSSComponent = (props) => {
    const { t: translate } = useTranslation();
    const {
        filters,
        eventLogLoadingStatus,
        selectedAsset,
        selectedOrganization,
        maintainSelectedEventData,
        selectedNotification,
        isAdmin
    } = props.model;

    const {
        setFilters,
        maintainEventData,
        setSelectedNotification
    } = props.actions;
    const selectedEventTypes = getSelectedEventData(maintainSelectedEventData, selectedAsset, selectedOrganization, filters.eventTypes, { name: 'eventTypes' });

    const eventStatusOptions = getEventStatusOptions(translate);

    const handleSelectDate = (from, to) => {
        if (from && to) {
            const filter = {
                ...filters,
                dateSelection: { from, to }
            };
            setFilters(filter);
        }
    };

    const handleFilterByEventType = (typeID) => {
        let eventTypes = [];
        eventTypes = selectState(filters.eventTypes, typeID);
        setFilters({ eventTypes });
        props.setIsExapnded(false);
        maintainEventData(eventTypes, selectedAsset, selectedOrganization, { name: 'eventTypes' });
    };

    // SET EVENT SEARCH FROM NOTIFICATION
    useEffect(() => {
        if (selectedNotification && isEventGroupNotification(selectedNotification) && !isCrossOrgNotification(selectedNotification, selectedOrganization)) {
            const searchTerm = selectedNotification?.properties?.eventMessage?.substring(0, 30);
            setFilters({ eventName: searchTerm });
            setSelectedNotification(null);
        }
    }, [selectedNotification, selectedOrganization]);

    useEffect(() => {
        if (selectedEventTypes) {
            setFilters({ eventTypes: selectedEventTypes });
        }
    }, [selectedEventTypes]);

    return (
        <Container className='event-log-filter-content'>
            <div className='filter-area'>
                <Row>
                    <Col className='ss-event-filter-date-selector' md='auto' >
                        <div className='time-label'>{translate('ABB.Powertrain.Frontend.occurredOnLbl')}</div>
                        <TimeSelectorRow
                            fromDate={filters?.dateSelection?.from}
                            toDate={filters?.dateSelection?.to}
                            handleSelectDate={handleSelectDate}
                            maxDaysRange={90}
                            maxDaysBack={isAdmin ? 365 : 90}
                            preSelectedDays={7}
                            numberOfMonths={2}
                            showLoading={false}
                            footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
                            disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                        />
                    </Col>
                    <Col className='event-log-filter' md='auto'>
                        <EventTypeFilter {...props}
                            selectedStates={filters.eventTypes}
                            selectState={handleFilterByEventType}
                            assetType={EventTypeAssetApplicability.SMART_SENSOR}
                            disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                        />
                    </Col>
                    <Col md='auto'>
                        <label htmlFor='event-status-filter' className='status-label'>{translate('ABB.Powertrain.Frontend.eventLogFilterStatusLabel')}</label>
                        <GeneralSelect id='status-ddn'
                            inputId='event-status-filter'
                            options={eventStatusOptions}
                            defaultValue={getDefaultEventStatus(filters.eventStatus || eventStatus.ALL, translate)}
                            onChange={(eventStatus) => setFilters({ eventStatus: eventStatus.id })}
                            getOptionLabel={option => option.title}
                            getOptionValue={option => option.id}
                            menuShouldScrollIntoView={false}
                            isSearchable={false}
                            isDisabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                        />
                    </Col>
                    <Col md='auto' className='ss-event-filter-search'>
                        <SearchBox
                            onChange={(searchTerm) => setFilters({ eventName: searchTerm })}
                            value={filters.eventName}
                            placeholder={translate('ABB.Powertrain.Frontend.searchEventByNameLbl')}
                            disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                        />
                    </Col>
                </Row>
            </div>
        </Container>
    );
};

EventLogFilterSSComponent.propTypes = {
    model: PropTypes.shape({
        filters: PropTypes.shape({
            eventTypes: PropTypes.arrayOf(PropTypes.string), // Array of selected event types
            eventStatus: PropTypes.string, // Selected event status
            eventName: PropTypes.string, // Search term for event name
            dateSelection: PropTypes.shape({
                from: PropTypes.string, // Start date of the selection
                to: PropTypes.string, // End date of the selection
            }), // Date selection range
        }), // Filters applied to the event log
        eventLogLoadingStatus: PropTypes.string, // Loading status for the event log
        selectedAsset: PropTypes.shape({
            id: PropTypes.string, // Selected asset ID
            name: PropTypes.string, // Selected asset name
        }), // Selected asset details
        selectedOrganization: PropTypes.shape({
            id: PropTypes.string, // Selected organization ID
            name: PropTypes.string, // Selected organization name
        }), // Selected organization details
        maintainSelectedEventData: PropTypes.bool, // Whether to maintain selected event data
        selectedNotification: PropTypes.shape({
            organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Organization id of the notification
            properties: PropTypes.shape({
                eventMessage: PropTypes.string, // Notification event message
            }),
        }), // Selected notification details
        isAdmin: PropTypes.bool, // Boolean indicating if the user is an admin
    }), // Model containing state data
    actions: PropTypes.shape({
        setFilters: PropTypes.func, // Function to set filters
        maintainEventData: PropTypes.func, // Function to maintain event data
        setSelectedNotification: PropTypes.func, // Function to set the selected notification
    }), // Actions object containing functions
    setIsExapnded: PropTypes.func, // Function to set the expanded state
};

export default EventLogFilterSSComponent;
